export const GUILD_WAR_PATH_REGISTRY = [
  {
    key: "bamboocut-kite",
    code: "kite",
    shortLabel: "Kite",
    fullLabel: "Bamboocut - Kite",
    roleTag: "DPS",
    description: "Fists over words",
    emoji: "🪁",
    sortOrder: 5,
  },
  {
    key: "bellstrike-splendor",
    code: "splendor",
    shortLabel: "Splendor",
    fullLabel: "Bellstrike - Splendor",
    roleTag: "DPS",
    description:
      "High-mobility sword/spear path focused on rapid repositioning and charged strike burst",
    emoji: "🛡️",
    sortOrder: 10,
  },
  {
    key: "bellstrike-umbra",
    code: "umbra",
    shortLabel: "Umbra",
    fullLabel: "Bellstrike - Umbra",
    roleTag: "DPS",
    description:
      "Sustained melee DPS that builds and detonates bleed stacks for burst windows",
    emoji: "⚔️",
    sortOrder: 20,
  },
  {
    key: "stonesplit-might",
    code: "might",
    shortLabel: "Might",
    fullLabel: "Stonesplit - Might",
    roleTag: "Tank",
    description:
      "Tank role focused on absorbing damage and controlling groups with AoE attacks and shields",
    emoji: "💪",
    sortOrder: 30,
  },
  {
    key: "stonesplit-strength",
    code: "strength",
    shortLabel: "Strength",
    fullLabel: "Stonesplit - Strength",
    roleTag: "DPS",
    description:
      "Single-target DPS with continuous damage buffs and unique Stagger mechanic",
    emoji: "🦾",
    sortOrder: 35,
  },
  {
    key: "silkbind-deluge",
    code: "deluge",
    shortLabel: "Deluge",
    fullLabel: "Silkbind - Deluge",
    roleTag: "Healer",
    description:
      "Dedicated healing path with powerful sustain, ally revival, and damage-boosting support skills",
    emoji: "💚",
    sortOrder: 40,
  },
  {
    key: "silkbind-jade",
    code: "jade",
    shortLabel: "Jade",
    fullLabel: "Silkbind - Jade",
    roleTag: "DPS",
    description:
      "Mobility-focused ranged DPS using aerial combos to deal damage",
    emoji: "💎",
    sortOrder: 50,
  },
  {
    key: "bamboocut-wind",
    code: "wind",
    shortLabel: "Wind",
    fullLabel: "Bamboocut - Wind",
    roleTag: "DPS",
    description: "Hyper-aggressive close-range DPS that overwhelms with speed",
    emoji: "🍃",
    sortOrder: 60,
  },
  {
    key: "bamboocut-dust",
    code: "dust",
    shortLabel: "Dust",
    fullLabel: "Bamboocut - Dust",
    roleTag: "DPS",
    description:
      "Crowd-control DPS specializing in multi-target engagements with ranged AoE and debuff management",
    emoji: "🌪️",
    sortOrder: 70,
  },
] as const

export type GuildWarPathEntry = (typeof GUILD_WAR_PATH_REGISTRY)[number]
export type MartialPathKey = GuildWarPathEntry["key"]
export type MartialPathCode = GuildWarPathEntry["code"]
export type GuildWarPathRoleTag = GuildWarPathEntry["roleTag"]
export type MartialSkillPath = MartialPathKey
export type MartialSkillPathCode = MartialPathCode

export const GUILD_WAR_PATH_ROLE_COLORS: Record<GuildWarPathRoleTag, string> = {
  Tank: "bg-blue-500/20 text-blue-700 dark:text-blue-300",
  DPS: "bg-red-500/20 text-red-700 dark:text-red-300",
  Healer: "bg-green-500/20 text-green-700 dark:text-green-300",
  // Support: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
  // Control: "bg-purple-500/20 text-purple-700 dark:text-purple-300",
}

const makeMap = <TKey extends string, TValue>(
  entries: ReadonlyArray<readonly [TKey, TValue]>,
  mapName: string,
) => {
  const map = new Map<TKey, TValue>()
  for (const [key, value] of entries) {
    if (map.has(key)) {
      throw new Error(`Duplicate key in ${mapName}: ${key}`)
    }
    map.set(key, value)
  }
  return map
}

const PATH_ENTRY_BY_KEY = makeMap(
  GUILD_WAR_PATH_REGISTRY.map((entry) => [entry.key, entry] as const),
  "PATH_ENTRY_BY_KEY",
)

const PATH_ENTRY_BY_CODE = makeMap(
  GUILD_WAR_PATH_REGISTRY.map((entry) => [entry.code, entry] as const),
  "PATH_ENTRY_BY_CODE",
)

export const MARTIAL_PATH_KEYS = GUILD_WAR_PATH_REGISTRY.map(
  (entry) => entry.key,
) as MartialPathKey[]

export const MARTIAL_PATH_CODES = GUILD_WAR_PATH_REGISTRY.map(
  (entry) => entry.code,
) as MartialPathCode[]

export const MARTIAL_PATH_SHORT_LABEL_BY_CODE: Record<MartialPathCode, string> =
  GUILD_WAR_PATH_REGISTRY.reduce(
    (acc, entry) => {
      acc[entry.code] = entry.shortLabel
      return acc
    },
    {} as Record<MartialPathCode, string>,
  )

export const MARTIAL_PATH_FULL_LABEL_BY_CODE: Record<MartialPathCode, string> =
  GUILD_WAR_PATH_REGISTRY.reduce(
    (acc, entry) => {
      acc[entry.code] = entry.fullLabel
      return acc
    },
    {} as Record<MartialPathCode, string>,
  )

export const MARTIAL_PATH_CODE_BY_KEY: Record<MartialPathKey, MartialPathCode> =
  GUILD_WAR_PATH_REGISTRY.reduce(
    (acc, entry) => {
      acc[entry.key] = entry.code
      return acc
    },
    {} as Record<MartialPathKey, MartialPathCode>,
  )

export const MARTIAL_PATH_KEY_BY_CODE: Record<MartialPathCode, MartialPathKey> =
  GUILD_WAR_PATH_REGISTRY.reduce(
    (acc, entry) => {
      acc[entry.code] = entry.key
      return acc
    },
    {} as Record<MartialPathCode, MartialPathKey>,
  )

export const isMartialPathKey = (value: string): value is MartialPathKey =>
  PATH_ENTRY_BY_KEY.has(value as MartialPathKey)

export const isMartialPathCode = (value: string): value is MartialPathCode =>
  PATH_ENTRY_BY_CODE.has(value as MartialPathCode)

export const getGuildWarPathByKey = (
  key: MartialPathKey,
): GuildWarPathEntry => {
  const entry = PATH_ENTRY_BY_KEY.get(key)
  if (!entry) {
    throw new Error(`Unknown guild war path key: ${key}`)
  }

  return entry
}

export const getGuildWarPathByCode = (
  code: MartialPathCode,
): GuildWarPathEntry => {
  const entry = PATH_ENTRY_BY_CODE.get(code)
  if (!entry) {
    throw new Error(`Unknown guild war path code: ${code}`)
  }

  return entry
}
